<template>
  <div class="content content_in">
    <template v-if="hasAccess">
      <form class="row">
        <div class="col-12 col-xl-6">
          <div class="title-row row justify-content-between">
            <div class="col-12 col-md-auto">
              <h1>
                {{ $t("platform.create_new") }}
              </h1>
            </div>
          </div>
          <VideoGuide class="d-xl-none" :title="$t('platform.how_to_create_platform_guide')" :description="$t('platform.video')" :videoLink="siteVideoGuide" />

          <div class="sep _h10"></div>

          <NameField v-model="platform.name" :v="$v.platform.name" :label="$t('platform.arbitrage.name')" />
          <PaymentModelField v-model="platform.payment_model" :invalid="$v.platform.payment_model.$error" />
          <TrafficFields :form="platform" :v="$v.platform" />
          <MailingFields :form="platform" :options="{ mailings, pushIntervals, pushDelays: push_delays }" />
          <PostbackFields :form="platform" :formatList="postback_format_list" :templates="templates" :invalid="$v.platform.postback_url.$invalid" />
          <SettingsField v-if="platform.payment_model && platform.payment_model.value === 0" :label="$t('platform.sales_on_feeds')" :hint="$t('platform.card.fields.feeds')">
            <CheckBoxWrapper v-model="platform.feedsSales" :text="$t(platform.feedsSales ? 'platform.card.on' : 'platform.card.off')" />
          </SettingsField>

          <div class="form-item pb-0 mt-4 d-none-mobile">
            <div class="form-item__field">
              <button type="submit" @click.prevent="createArbitrage" class="btn _mob100">
                {{ $t("platform.save_platform") }}
              </button>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-6 h-fit d-flex flex-wrap">
          <VideoGuide class="d-none d-xl-block" :title="$t('platform.how_to_create_platform_guide')" :description="$t('platform.video')" :videoLink="siteVideoGuide" />
          <LandingsPicker v-model="platform.lands" :landings="landings" :invalid="$v.platform.lands.$error" />
        </div>

        <div class="row justify-content-between mx-0 mt-3 d-xl-none w-100">
          <div class="col-12 col-sm-4 col-md-5">
            <div class="form-item pb-0">
              <div class="form-item__field">
                <button type="submit" @click.prevent="createArbitrage" class="btn _mob100">
                  {{ $t("platform.save_platform") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </template>
    <template v-else-if="!hasAccess && !showModeration">
      <ModerationBlank @send="showModeration = true" />
    </template>
    <template v-else-if="showModeration">
      <ModerationStatus :title="statusTitle" :reason="statusText" :approve_status="user && user.is_moderation_form_exist" />
    </template>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import ServiceSubAccs from "@/api/subaccsService";
import serviceDomain from "@/api/serviceDomain";
import ServicePanel from "@/api/servicePanel";
import ModerationBlank from "@/components/ModerationBlank.vue";
import ModerationStatus from "@/components/ModerationStatus.vue";
import VideoGuide from "@/components/VideoGuide.vue";
import CheckBoxWrapper from "@/components/CheckBoxWrapper.vue";
import "@/assets/css/_page-platform.scss";
import "@/assets/css/_page-add-platform.scss";
import "@/assets/css/_section.scss";
import { validationMixin } from "vuelidate";
import { required, minLength, maxLength, url, requiredIf } from "vuelidate/lib/validators";
import SettingsField from "@/components/PlatformCard/SettingsField.vue";
import NameField from "@/components/PlatformCard/fields/NameField.vue";
import PaymentModelField from "@/components/PlatformCard/fields/PaymentModelField.vue";
import TrafficFields from "@/components/PlatformCard/fields/TrafficFields.vue";
import MailingFields from "@/components/PlatformCard/fields/MailingFields.vue";
import PostbackFields from "@/components/PlatformCard/fields/PostbackFields.vue";
import LandingsPicker from "@/components/PlatformCard/fields/LandingsPicker.vue";
import { newArbitragePayload, postbackFormatList, KIND_ARBITRAGE, REDIRECT_MODES, ROTATION_MODES } from "@/components/PlatformCard/subaccountPayload";

// Creates an arbitrage platform. Its settings are changed later on the platform card.
export default {
  name: "Arbitrage",
  components: {
    ModerationBlank,
    ModerationStatus,
    VideoGuide,
    CheckBoxWrapper,
    SettingsField,
    NameField,
    PaymentModelField,
    TrafficFields,
    MailingFields,
    PostbackFields,
    LandingsPicker,
  },
  mixins: [validationMixin],
  data() {
    return {
      showModeration: false,
      hasAccess: true,

      landings: [],
      platform: {
        schedule_days: [],
        complexity: 3,
        lands: [],
        fullscreen: false,
        name: "",
        redirect_type: REDIRECT_MODES[1],
        domain_rotation: ROTATION_MODES[0],
        payment_model: {
          title: "Revshare",
          value: 0,
        },
        tb_subscription: "default",
        tb_complexity: "partners",
        tb_subscription_url: "",
        tb_complexity_url: "",
        postback_url: "",
        postback_format: [],
        enable_onclick_ads: false,
        redirect_to_actual_domain: true,
        enable_in_page_ads: true,
        feedsSales: false,
        push_send_interval: {},
        mailing_frequency: { title: "Standard", value: "standart" },
        push_delay_interval: {},
      },
      mailings: [],
      templates: [],
      postback_format_list: postbackFormatList(KIND_ARBITRAGE),
      pushIntervals: [],
      push_delays: [],
    };
  },
  validations: {
    platform: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(52),
      },
      tb_subscription_url: {
        required: requiredIf(function () {
          return this.platform.tb_subscription === "user";
        }),
        url,
        maxLength: maxLength(1024),
      },
      tb_complexity_url: {
        required: requiredIf(function () {
          return this.platform.tb_complexity === "user";
        }),
        url,
        maxLength: maxLength(1024),
      },
      postback_url: {
        url,
        maxLength: maxLength(4096),
      },
      lands: {
        minLength: (value) => {
          return value.length >= 1;
        },
      },
      payment_model: {
        required,
      },
    },
  },
  watch: {
    "$i18n.locale": function () {
      this.getPushDelays();
      this.getPushIntervals();
    },
  },
  async mounted() {
    if (!this.user?.confirm) {
      this.hasAccess = false;
      if (this.user?.is_moderation_form_exist) this.showModeration = true;
      if (!this.user?.is_moderation_form_exist) this.showModeration = false;
      return;
    }
    await this.getMailingFrequency();
    await this.getPushIntervals();
    await this.getPushDelays();
    await this.getUrlTemplates();

    ServiceSubAccs.getLandings().then((res) => {
      if (res && res.status === 200) {
        this.landings = res.data;
      }
    });
  },
  methods: {
    getPushIntervals() {
      if (this.featureFlags && this.featureFlags.includes("custom_push_send_interval")) {
        return serviceDomain.getPushIntervals().then((res) => {
          if (res && res.status === 200) {
            this.pushIntervals = res.data;
            this.platform.push_send_interval = this.pushIntervals[0];
          } else {
            this.$alert({
              type: "error",
              title: this.$t("sent_error"),
              text: res?.message || "",
            });
          }
        });
      }
    },
    getUrlTemplates() {
      return ServicePanel.getUrlTemplates().then((res) => {
        if (res && res.status === 200) {
          this.templates = res.data;
        }
      });
    },
    getPushDelays() {
      if (this.featureFlags && this.featureFlags.includes("push_delay_interval")) {
        return ServicePanel.getPushDelays().then((res) => {
          if (res && res.status === 200) {
            this.push_delays = res.data;
            this.platform.push_delay_interval = this.push_delays[0];
          }
        });
      }
    },
    getMailingFrequency() {
      return ServicePanel.getMailingFrequency().then((res) => {
        if (res && res.status === 200) {
          this.mailings = res.data;
        }
      });
    },
    createArbitrage() {
      this.$v.$touch();

      if (this.checkError) {
        this.$gtm.trackEvent({ event: "create_source_disabled_button" });
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        return;
      }

      this.$gtm.trackEvent({ event: "create_source_submited" });

      ServiceSubAccs.createSubAcc(newArbitragePayload(this.platform)).then((res) => {
        if (res) {
          if (res.status === 200) {
            this.$gtm.trackEvent({ event: "create_source_success" });
            this.$alert({
              title: this.$t("sent_success"),
              text: res.message,
              type: "success",
            });
            this.$router.push("/platforms");
          } else {
            this.$gtm.trackEvent({ event: "create_source_failure" });
            this.$alert({
              title: this.$t("sent_error"),
              text: res.message,
            });
          }
        }
      });
    },
  },
  computed: {
    ...mapGetters({
      user: "user/getUser",
      featureFlags: "user/getFeatureFlags",
    }),

    siteVideoGuide() {
      return this.$i18n.locale === "ru" ? "https://youtu.be/aVCowBx33FA" : "https://youtu.be/OQkjFenNb9E";
    },
    // moderation block
    statusTitle() {
      if (this.user?.moderation_form_reject_reason) return this.$t("platform.application_fail");
      else return this.$t("platform.application_sent");
    },
    statusText() {
      if (this.user?.moderation_form_reject_reason) return this.user.moderation_form_reject_reason;
      else return this.$t("platform.application_wait");
    },
    checkError() {
      const generalError = this.$v.platform.lands.$invalid || this.$v.platform.name.$invalid || this.$v.platform.payment_model.$invalid || this.$v.platform.postback_url.$invalid;
      if (this.platform.tb_complexity !== "user" && this.platform.tb_subscription !== "user") {
        return generalError;
      } else if (this.platform.tb_complexity !== "user" && this.platform.tb_subscription === "user") {
        return generalError || this.$v.platform.tb_subscription_url.$invalid;
      } else if (this.platform.tb_complexity === "user" && this.platform.tb_subscription !== "user") {
        return generalError || this.$v.platform.tb_complexity_url.$invalid;
      } else {
        return this.$v.$invalid;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@media screen and (max-width: 1200px) {
  .d-none-mobile {
    display: none;
  }
}

.h-fit {
  height: fit-content;
}
</style>
